import React from 'react';
import { connect } from 'react-redux';
import { Box } from '@mui/material';
import { Helmet } from 'react-helmet';
import config from '@configFile';
import {
  seoOrganization,
  getHelmetMeta,
  seoWebsite,
  authorSchema,
  getPageImageSeo
} from '@utils/seo';
import { getPlainAffiliateLink } from '@utils/linkUtils';
import PageContainer from '@components/PageContainer';
import {
  // getCurrentDealEventStrObj,
  getPostPrefix,
  parseTitle,
  getCurrentDealEventStrObj
} from '@utils/index';
import Disclaimer from '@components/Disclaimer';
import { sidebarStyle } from '@utils/theme';
import SiteSidebar from '@components/SiteSidebar';
import { AppState } from '@types';

interface BlogContainerProps extends ReturnType<typeof mapStateToProps> {
  children: React.ReactNode;
  renderInContainer: boolean;
  shouldRenderSEO?: boolean;
  linkSlug?: string | null;
}

const BlogContainer = ({
  children,
  renderInContainer,
  shouldRenderSEO = true,
  blogPageSEO,
  linkSlug
}: BlogContainerProps) => {
  if (!renderInContainer) {
    return <>{children}</>;
  }

  const { authorLink } = config.admin;

  const currentEventObj = getCurrentDealEventStrObj();
  const isPageDeal = !!blogPageSEO?.pageDeal;
  const isProductPage = !!blogPageSEO?.dealSummary?.ASIN;
  const titlePrefix =
    currentEventObj?.label && blogPageSEO?.isEventDeal
      ? `${currentEventObj?.label} Deal`
      : 'Amazon Deal';

  const blogTitlePage = blogPageSEO?.dealSummary?.title || blogPageSEO?.title;
  const isCustomBlogPost = !!blogPageSEO?.customBlogPost;
  const customBlogPostTitle = blogPageSEO?.customBlogPost?.title;

  const title =
    blogTitlePage && !isCustomBlogPost && !isPageDeal
      ? `${titlePrefix} on ${parseTitle(blogTitlePage) || ''} `
      : `${customBlogPostTitle || blogTitlePage || 'Jungle Deals Blog'}`;

  const contentType =
    isCustomBlogPost || isProductPage ? 'article' : 'collection';

  const SEOtitle = `${title}`.trim();

  let currentUrl = isPageDeal
    ? `${config.SITE_URL}/p/${blogPageSEO?.slug}`
    : `${config.SITE_URL}/blog/${blogPageSEO?.slug}`;

  if (!blogPageSEO?.slug) {
    currentUrl = `${config.SITE_URL}/blog`;
  }

  const breadCrumbs = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: `${config.SITE_URL}`
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Blog',
      item: `${config.SITE_URL}/blog`
    }
  ];

  if (blogPageSEO?.slug) {
    breadCrumbs.push({
      '@type': 'ListItem',
      position: 3,
      name: SEOtitle,
      item: currentUrl
    });
  }

  const seoImage = blogPageSEO?.image;
  const blogTitle = `Amazon Deals Blog featuring coupons, hidden discounts & More`;

  const getProductSnippet = () => {
    if (
      blogPageSEO?.dealSummary?.isExpired ||
      !blogPageSEO?.dealSummary?.finalPrice
    ) {
      return [];
    }

    const imageBlock =
      blogPageSEO?.dealSummary?.image600Url &&
      blogPageSEO?.dealSummary?.image600Height &&
      blogPageSEO?.dealSummary?.image600Width
        ? {
            image: {
              '@context': 'https://schema.org',
              '@type': 'ImageObject',
              url: blogPageSEO?.dealSummary?.image600Url,
              height: blogPageSEO?.dealSummary?.image600Height,
              width: blogPageSEO?.dealSummary?.image600Width
            }
          }
        : null;

    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        '@id': `https://www.jungle.deals/blog/${blogPageSEO?.dealSummary?.slug}/#product`,
        url: `https://www.jungle.deals/blog/${blogPageSEO?.dealSummary?.slug}`,
        name: parseTitle(blogPageSEO?.dealSummary?.title),
        ...(imageBlock || {}),
        description: (blogPageSEO?.dealSummary?.postText || '').slice(0, 160),
        brand: {
          '@type': 'Brand',
          name: blogPageSEO?.dealSummary?.brand
        },
        sku: blogPageSEO?.dealSummary?.ASIN,
        mpn: blogPageSEO?.dealSummary?.ASIN,
        ...(blogPageSEO?.dealSummary?.isExpired ||
        !blogPageSEO?.dealSummary?.finalPrice
          ? {}
          : {
              offers: {
                '@context': 'https://schema.org',
                '@type': 'Offer',
                url: getPlainAffiliateLink(blogPageSEO?.dealSummary?.ASIN),
                priceCurrency: 'USD',
                seller: {
                  '@context': 'https://schema.org',
                  '@type': 'Organization',
                  name: 'Amazon',
                  url: 'https://www.amazon.com'
                },
                ...(blogPageSEO?.dealSummary?.isExpired
                  ? { availability: 'https://schema.org/OutOfStock' }
                  : {
                      price: blogPageSEO?.dealSummary?.finalPrice,
                      availability: 'https://schema.org/InStock'
                    })
              }
            })
      }
    ];
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      isProductPage
        ? {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            '@id': `${config.SITE_URL}/blog/${blogPageSEO?.dealSummary?.slug}/#blogPost`,
            mainEntityOfPage: {
              '@context': 'https://schema.org',
              '@type': 'WebPage',
              '@id': `${config.SITE_URL}/blog/${blogPageSEO?.dealSummary?.slug}`
            },
            isPartOf: {
              '@id': `${config.SITE_URL}/#website`
            },
            headline: SEOtitle || blogTitle,
            description: 'Quick overview of the deal',
            articleBody: `${(blogPageSEO?.dealSummary?.postText || '').slice(
              0,
              160
            )}...`,
            ...(seoImage
              ? {
                  image: {
                    '@id': `${currentUrl}/#primaryimage`
                  },
                  thumbnailUrl: `${seoImage}`
                }
              : {}),
            datePublished:
              blogPageSEO?.datePublished || new Date().toISOString(),
            dateModified: blogPageSEO?.dateModified || new Date().toISOString(),
            author: {
              '@context': 'https://schema.org',
              '@type': 'Person',
              '@id': `${config.SITE_URL}${authorLink}/#author`
            },
            publisher: {
              '@context': 'https://schema.org',
              '@type': 'Organization',
              '@id': `${config.SITE_URL}/#organization`
            },
            ...(blogPageSEO?.dealSummary?.isExpired ||
            !blogPageSEO?.dealSummary?.finalPrice
              ? {}
              : {
                  about: {
                    '@context': 'https://schema.org',
                    '@type': 'Product',
                    '@id': `${config.SITE_URL}/blog/${blogPageSEO?.dealSummary?.slug}/#product`
                  }
                }),
            keywords: [
              'Amazon Deals',
              ...(blogPageSEO?.dealSummary?.brand
                ? [
                    blogPageSEO?.dealSummary?.brand,
                    `Amazon deal on ${blogPageSEO?.dealSummary?.brand}`
                  ]
                : [])
            ]
          }
        : {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            '@id': `${currentUrl}`,
            url: `${currentUrl}`,
            name: SEOtitle || blogTitle,
            isPartOf: {
              '@id': `${config.SITE_URL}/#website`
            },
            ...(seoImage
              ? {
                  primaryImageOfPage: {
                    '@id': `${currentUrl}/#primaryimage`
                  },
                  image: {
                    '@id': `${currentUrl}/#primaryimage`
                  },
                  thumbnailUrl: `${seoImage}`
                }
              : {}),
            datePublished:
              blogPageSEO?.datePublished || new Date().toISOString(),
            dateModified: blogPageSEO?.dateModified || new Date().toISOString(),
            description: `${blogPageSEO?.description || ''}`,
            breadcrumb: {
              '@id': `${currentUrl}/#breadcrumb`
            },
            inLanguage: 'en-US',
            potentialAction: [
              {
                '@context': 'https://schema.org',
                '@type': 'ReadAction',
                target: [`${currentUrl}`]
              }
            ]
          },
      ...(seoImage ? [getPageImageSeo(seoImage, currentUrl)] : []),
      ...(isProductPage ? getProductSnippet() : []),
      {
        '@type': 'BreadcrumbList',
        '@id': `${currentUrl}/#breadcrumb`,
        name: `${SEOtitle || blogTitle}`,
        '@context': 'https://schema.org',
        itemListElement: breadCrumbs
      },
      {
        ...seoWebsite
      },
      {
        ...seoOrganization
      },
      {
        ...authorSchema
      }
    ]
  };

  const shouldHideBlogTitle = isPageDeal || isProductPage || isCustomBlogPost;

  const preloadImages = (blogPageSEO?.preloadImages || []).map((image) => ({
    rel: 'preload',
    as: 'image',
    href: image
  }));

  return (
    <Box id="top">
      <Box
        sx={{
          display: 'flex',

          '@media (max-width: 900px)': {
            display: 'block'
          }
        }}
      >
        <Box
          sx={{
            width: '62%',
            '@media (max-width: 1200px)': {
              width: '59%'
            },
            '@media (max-width: 900px)': {
              width: '100%'
            }
          }}
        >
          <PageContainer
            headingComponent={shouldRenderSEO ? 'h1' : 'h2'}
            title={
              shouldHideBlogTitle
                ? undefined
                : `${getPostPrefix()}Live Jungle Deals Blog`
            }
            fullWidth
            isLive={!linkSlug}
          >
            {shouldRenderSEO ? (
              <Helmet
                title={SEOtitle || blogTitle}
                titleTemplate={
                  isCustomBlogPost ? `%s — Jungle Deals Blog` : `%s`
                }
                meta={getHelmetMeta({
                  contentType,
                  ogPath: blogPageSEO?.ogPath,
                  title: SEOtitle || blogTitle,
                  description:
                    blogPageSEO?.description ||
                    'Latest Amazon Deals - See what just came on sale on Amazon. Coupons, hidden discounts, and more',
                  image: blogPageSEO?.image || '',
                  imageWidth: `${blogPageSEO?.imageWidth || 800}`,
                  imageHeight: `${blogPageSEO?.imageHeight || 275}`,
                  publishTime:
                    blogPageSEO?.datePublished ||
                    new Date(new Date().setHours(4, 0, 0)).toISOString()
                })}
                // add canonical link to the page
                link={
                  blogPageSEO?.slug
                    ? [
                        {
                          rel: 'canonical',
                          href: currentUrl
                        },
                        ...preloadImages
                      ]
                    : [
                        {
                          rel: 'canonical',
                          href: `${config.SITE_URL}/blog`
                        }
                      ]
                }
                script={[
                  {
                    type: 'application/ld+json',
                    innerHTML: JSON.stringify(jsonLd)
                  }
                ]}
              />
            ) : null}
            <Box
              sx={{
                marginBottom: {
                  xs: '16px',
                  sm: '8px'
                }
              }}
            >
              <Disclaimer />
            </Box>
            {children}
          </PageContainer>
        </Box>
        <Box sx={sidebarStyle}>
          <SiteSidebar shouldRenderTopPadding={shouldRenderSEO} />
        </Box>
      </Box>
    </Box>
  );
};

const mapStateToProps = ({ home: { blogPageSEO } }: AppState) => {
  return {
    blogPageSEO
  };
};

export default connect(mapStateToProps)(BlogContainer);
